import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Card,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Typography,
  Switch,
  Popconfirm,
  AutoComplete,
} from 'antd'
import { PlusOutlined, ThunderboltOutlined, DeleteOutlined } from '@ant-design/icons'
import ruleApi from '../../api/rules'
import type { RemedyRule } from '../../types'

const { Title } = Typography
const { TextArea } = Input
const { Option } = Select

const LITIGATION_STAGES = [
  { value: 'first_instance', label: '一审' },
  { value: 'second_instance', label: '二审' },
  { value: 'retrial', label: '再审' },
  { value: 'protest', label: '抗诉' },
]

const EXECUTION_STAGES = [
  { value: 'execution_application', label: '申请执行' },
  { value: 'execution_acceptance', label: '执行立案' },
  { value: 'execution_implementation', label: '执行实施' },
  { value: 'execution_objection', label: '执行异议' },
  { value: 'execution_reconsideration', label: '执行复议' },
  { value: 'execution_supervision', label: '执行监督' },
]

const getStageOptions = (caseType: string) =>
  caseType === 'execution' ? EXECUTION_STAGES : LITIGATION_STAGES

const Rules: React.FC = () => {
  const [rules, setRules] = useState<RemedyRule[]>([])
  const [loading, setLoading] = useState(true)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRule, setEditingRule] = useState<RemedyRule | null>(null)
  const [form] = Form.useForm()
  const selectedCaseType = Form.useWatch('case_type', form)

  useEffect(() => {
    loadRules()
  }, [])

  const loadRules = async () => {
    setLoading(true)
    try {
      const data = await ruleApi.getList(0, 100)
      setRules(data)
    } catch (error) {
      console.error('加载规则失败:', error)
      message.error('加载规则失败')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingRule(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (rule: RemedyRule) => {
    setEditingRule(rule)
    form.setFieldsValue({
      case_type: rule.case_type,
      prev_stage: rule.prev_stage,
      prev_result: rule.prev_result,
      remedy_type: rule.remedy_type,
      authority: rule.authority,
      deadline_days: rule.deadline_days,
      materials_required: rule.materials_required,
      legal_basis: rule.legal_basis,
      enabled: rule.enabled,
    })
    setModalVisible(true)
  }

  const handleToggle = async (rule: RemedyRule) => {
    try {
      await ruleApi.toggle(rule.id)
      message.success(rule.enabled ? '已停用规则' : '已启用规则')
      loadRules()
    } catch (error) {
      message.error('操作失败')
    }
  }

  const handleDelete = async (ruleId: number) => {
    try {
      await ruleApi.delete(ruleId)
      message.success('删除成功')
      loadRules()
    } catch (error) {
      message.error('删除失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (editingRule) {
        await ruleApi.update(editingRule.id, values)
        message.success('更新成功')
      } else {
        await ruleApi.create(values)
        message.success('创建成功')
      }
      setModalVisible(false)
      loadRules()
    } catch (error: any) {
      if (error.errorFields) {
        return
      }
      message.error('操作失败')
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '案件类型',
      dataIndex: 'case_type',
      key: 'case_type',
      width: 100,
      render: (type: string) => {
        const colors: any = {
          civil: 'blue',
          criminal: 'red',
          administrative: 'green',
          execution: 'orange',
        }
        const labels: any = {
          civil: '民事',
          criminal: '刑事',
          administrative: '行政',
          execution: '执行',
        }
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>
      },
    },
    {
      title: '前置阶段',
      dataIndex: 'prev_stage',
      key: 'prev_stage',
      width: 100,
      render: (stage: string) => {
        const labels: any = {
          first_instance: '一审',
          second_instance: '二审',
          retrial: '再审',
          protest: '抗诉',
        }
        return labels[stage] || stage
      },
    },
    {
      title: '前置结果',
      dataIndex: 'prev_result',
      key: 'prev_result',
      width: 120,
    },
    {
      title: '救济途径',
      dataIndex: 'remedy_type',
      key: 'remedy_type',
      width: 150,
      ellipsis: true,
    },
    {
      title: '管辖机关',
      dataIndex: 'authority',
      key: 'authority',
      width: 150,
      ellipsis: true,
    },
    {
      title: '期限 (天)',
      dataIndex: 'deadline_days',
      key: 'deadline_days',
      width: 80,
      align: 'center' as const,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      align: 'center' as const,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>
          {enabled ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: RemedyRule) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Switch
            size="small"
            checked={record.enabled}
            onChange={() => handleToggle(record)}
            checkedChildren="启"
            unCheckedChildren="停"
          />
          <Popconfirm
            title="确定要删除此规则吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              <ThunderboltOutlined style={{ marginRight: 8 }} />
              救济规则管理
            </Title>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            添加规则
          </Button>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={rules}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingRule ? '编辑规则' : '添加规则'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          size="large"
        >
          <Form.Item
            label="案件类型"
            name="case_type"
            rules={[{ required: true, message: '请选择案件类型' }]}
          >
            <Select placeholder="请选择">
              <Option value="civil">民事案件</Option>
              <Option value="criminal">刑事案件</Option>
              <Option value="administrative">行政案件</Option>
              <Option value="execution">执行案件</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="前置阶段"
            name="prev_stage"
            rules={[{ required: true, message: '请输入或选择前置阶段' }]}
          >
            <AutoComplete
              placeholder="可选择或手动输入前置程序（如：执行异议、执行复议）"
              options={getStageOptions(selectedCaseType)}
              allowClear
              filterOption={(inputValue, option) =>
                (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="前置结果"
            name="prev_result"
            rules={[{ required: true, message: '请输入前置结果' }]}
          >
            <Input placeholder="如：判决、裁定驳回、裁定不予受理" />
          </Form.Item>

          <Form.Item
            label="救济途径"
            name="remedy_type"
            rules={[{ required: true, message: '请输入救济途径' }]}
          >
            <Input placeholder="如：上诉（二审）、申请再审、申请检察监督" />
          </Form.Item>

          <Form.Item
            label="管辖机关"
            name="authority"
            rules={[{ required: true, message: '请输入管辖机关' }]}
          >
            <Input placeholder="如：上一级人民法院" />
          </Form.Item>

          <Form.Item
            label="法定期限（天）"
            name="deadline_days"
            rules={[{ required: true, message: '请输入期限天数' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="如：15、180" />
          </Form.Item>

          <Form.Item
            label="所需材料"
            name="materials_required"
          >
            <TextArea
              rows={3}
              placeholder="如：上诉状、一审判决书复印件、身份证明"
            />
          </Form.Item>

          <Form.Item
            label="法律依据"
            name="legal_basis"
            rules={[{ required: true, message: '请输入法律依据' }]}
          >
            <TextArea
              rows={2}
              placeholder="如：《中华人民共和国民事诉讼法》第一百七十一条"
            />
          </Form.Item>

          <Form.Item
            label="启用状态"
            name="enabled"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Rules
